import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc,
  query, 
  where, 
  orderBy, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

/**
 * Notification Service
 * Handles creating, reading, updating, and deleting notifications
 */
export const notificationService = {
  /**
   * Create a new notification
   * @param {Object} notificationData - The notification data
   * @returns {Object} { id, error }
   */
  async createNotification(notificationData) {
    try {
      const docRef = await addDoc(collection(db, 'notifications'), {
        ...notificationData,
        read: false,
        createdAt: serverTimestamp(),
      });
      return { id: docRef.id };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { error: error.message };
    }
  },

  /**
   * Get all notifications for a user
   * @param {string} userId - The user ID
   * @param {boolean} unreadOnly - Only fetch unread notifications
   * @returns {Object} { notifications, error }
   */
  async getUserNotifications(userId, unreadOnly = false) {
    try {
      const notificationsRef = collection(db, 'notifications');
      let q = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      if (unreadOnly) {
        q = query(
          notificationsRef,
          where('userId', '==', userId),
          where('read', '==', false),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const notifications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        dueDate: doc.data().dueDate?.toDate(),
      }));

      return { notifications };
    } catch (error) {
      console.error('Error getting user notifications:', error);
      return { notifications: [], error: error.message };
    }
  },

  /**
   * Get unread notification count for a user
   * @param {string} userId - The user ID
   * @returns {Object} { count, error }
   */
  async getUnreadCount(userId) {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return { count: querySnapshot.size };
    } catch (error) {
      console.error('Error getting unread count:', error);
      return { count: 0, error: error.message };
    }
  },

  /**
   * Mark a notification as read
   * @param {string} notificationId - The notification ID
   * @returns {Object} { success, error }
   */
  async markAsRead(notificationId) {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Mark all notifications as read for a user
   * @param {string} userId - The user ID
   * @returns {Object} { success, error }
   */
  async markAllAsRead(userId) {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const updatePromises = querySnapshot.docs.map(doc =>
        updateDoc(doc.ref, {
          read: true,
          readAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);
      return { success: true };
    } catch (error) {
      console.error('Error marking all as read:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a notification
   * @param {string} notificationId - The notification ID
   * @returns {Object} { success, error }
   */
  async deleteNotification(notificationId) {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Create course deadline notifications when user starts a course
   * @param {string} userId - The user ID
   * @param {Object} course - The course object with deadline info
   * @returns {Object} { success, error }
   */
  async createCourseDeadlineNotifications(userId, course) {
    try {
      if (!course.deadlines || !course.deadlines.enabled) {
        return { success: true }; // No deadlines configured
      }

      const notifications = [];
      const now = new Date();

      // Create notification for overall course deadline
      if (course.deadlines.courseDuration) {
        const dueDate = new Date(now);
        dueDate.setDate(dueDate.getDate() + course.deadlines.courseDuration);

        notifications.push({
          userId,
          type: 'course_deadline',
          title: `Course Deadline: ${course.title}`,
          message: `You have ${course.deadlines.courseDuration} days to complete "${course.title}"`,
          courseId: course.id,
          dueDate: Timestamp.fromDate(dueDate),
          priority: 'medium',
          actionUrl: '/courses',
        });
      }

      // Create notifications for chapter deadlines
      if (course.chapters && course.deadlines.chapterDuration) {
        course.chapters.forEach((chapter, index) => {
          const chapterDueDate = new Date(now);
          chapterDueDate.setDate(chapterDueDate.getDate() + (course.deadlines.chapterDuration * (index + 1)));

          notifications.push({
            userId,
            type: 'chapter_deadline',
            title: `Chapter Deadline: ${chapter.title}`,
            message: `Complete "${chapter.title}" by ${chapterDueDate.toLocaleDateString()}`,
            courseId: course.id,
            chapterId: chapter.id,
            dueDate: Timestamp.fromDate(chapterDueDate),
            priority: 'low',
            actionUrl: '/courses',
          });
        });
      }

      // Create all notifications
      const createPromises = notifications.map(notification =>
        this.createNotification(notification)
      );

      await Promise.all(createPromises);
      return { success: true, count: notifications.length };
    } catch (error) {
      console.error('Error creating course deadline notifications:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get upcoming deadlines for a user
   * @param {string} userId - The user ID
   * @param {number} daysAhead - Number of days to look ahead (default 7)
   * @returns {Object} { deadlines, error }
   */
  async getUpcomingDeadlines(userId, daysAhead = 7) {
    try {
      const now = new Date();
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysAhead);

      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('type', 'in', ['course_deadline', 'chapter_deadline', 'assignment_deadline']),
        where('dueDate', '>=', Timestamp.fromDate(now)),
        where('dueDate', '<=', Timestamp.fromDate(futureDate)),
        orderBy('dueDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const deadlines = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        dueDate: doc.data().dueDate?.toDate(),
      }));

      return { deadlines };
    } catch (error) {
      console.error('Error getting upcoming deadlines:', error);
      return { deadlines: [], error: error.message };
    }
  },

  /**
   * Check for overdue items and create notifications
   * @param {string} userId - The user ID
   * @returns {Object} { success, overdueCount, error }
   */
  async checkOverdueItems(userId) {
    try {
      const now = new Date();
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('type', 'in', ['course_deadline', 'chapter_deadline', 'assignment_deadline']),
        where('dueDate', '<', Timestamp.fromDate(now)),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const overdueItems = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().dueDate?.toDate(),
      }));

      // Update priority to high for overdue items
      const updatePromises = overdueItems.map(item =>
        updateDoc(doc(db, 'notifications', item.id), {
          priority: 'high',
          overdue: true,
        })
      );

      await Promise.all(updatePromises);

      return { success: true, overdueCount: overdueItems.length };
    } catch (error) {
      console.error('Error checking overdue items:', error);
      return { success: false, overdueCount: 0, error: error.message };
    }
  },
};

